import React, { ReactNode, useContext } from "react";
import { AlertBox, BoundedContent } from "@vp/swan";
import { Translations } from "../utils/Translation";
import { Logger } from "../logging/logger";
import { IssuesWrapper } from "@vp/gatsby-pages-issues-tracker-plugin";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";

interface ErrorBoundaryProps {
  component?: string;
  children?: ReactNode | ReactNode[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: { name: "", message: "", stack: "" } };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error: Error) {
    const { mpv, locale } = useContext(PageStaticDataContext);

    if (this.props.component === "ProductBuilder") {
      Logger.instance.error("Cannot proceed from product page - Product Builder broken", {
        mpvId: mpv.mpvId,
        locale,
        message: error.message,
        stack: error.stack,
      });
    } else {
      Logger.instance.error("Component error", {
        message: error.message,
        stack: error.stack,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      const componentErrorEntry = Translations.getInstance().getTranslationEntry("componentError");
      return (
        <BoundedContent my={"5"}>
          <IssuesWrapper targetNodes={[componentErrorEntry]} elementName="Component Error">
            <AlertBox skin="error">{componentErrorEntry ?? ""}</AlertBox>
          </IssuesWrapper>
        </BoundedContent>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
